import { makeObservable, observable, computed, action } from 'mobx'
import { MicCheck } from './MicCheck'
import { browserMicCheckDeps } from './browserDeps'
import type { MeetingCubit } from '../../MeetingCubit'
import {
  accrueElapsed,
  firstSpeechStartInWindow,
  heartbeatObservation,
  shouldConfirmDelivery,
  soloSafeguardTripped,
  totalSpeechInWindow,
  transcriptWaitExpired,
  uncaptionedServerSpeechMS,
  type HeartbeatState,
  type SpeechInterval,
} from './rules'
import type {
  HeartbeatObservationKind,
  MicReport,
  Problem,
  ProblemCause,
  ProblemLayer,
  ResolvedBy,
  SecretaryStatus,
  SoundcheckConfig,
} from './types'
import {
  NOOP_TELEMETRY,
  createSoundcheckTelemetry,
  type SoundcheckTelemetry,
} from './telemetry'

export type SoundcheckControllerDeps = {
  // Production convenience: the meeting supplies the settings (for config), the
  // passive logger (for telemetry with the room envelope) and, by default, the
  // browser MicCheck. Everything else is derived from it.
  meeting?: Pick<MeetingCubit, 'repository' | 'logEventPassive'>
  // Explicit overrides, used by tests and any host without a meeting. When a
  // meeting is not supplied, `config` is required.
  config?: () => SoundcheckConfig
  mic?: MicCheck
  telemetry?: SoundcheckTelemetry
  // Clock and timers default to the real ones; tests inject a fake clock.
  now?: () => number
  setTimer?: (fn: () => void, ms: number) => ReturnType<typeof setTimeout>
  clearTimer?: (handle: ReturnType<typeof setTimeout>) => void
}

type NewlyConfirmed = {
  layer: ProblemLayer
  cause: ProblemCause
  evidence: {
    speechMS?: number
    serverConfirmations?: number
    msSincePause?: number
    soloMS?: number
    notListeningMS?: number
    serverSpeechMS?: number
  }
}

/**
 * Combines every report and makes every health decision. It keeps at most one
 * unresolved problem per layer — microphone, delivery, transcript — recomputes
 * priority on every evaluation, and owns the dialog/banner presentation state.
 * Nothing else decides that transcription has failed.
 */
export class SoundcheckController {
  readonly mic: MicCheck

  microphoneProblem: Problem | null = null
  deliveryProblem: Problem | null = null
  transcriptProblem: Problem | null = null

  /** The one the UI shows. Recomputed on every evaluation. */
  problem: Problem | null = null

  private dialogOpen = false

  private readonly config: () => SoundcheckConfig
  private readonly now: () => number
  private readonly setTimer: (
    fn: () => void,
    ms: number
  ) => ReturnType<typeof setTimeout>

  private readonly clearTimer: (handle: ReturnType<typeof setTimeout>) => void
  private readonly telemetry: SoundcheckTelemetry
  private tickTimer: ReturnType<typeof setTimeout> | null = null
  private disposed = false

  // Microphone.
  private micReport: MicReport = { state: 'checking', reason: 'starting' }
  // When the mic last became ready; null while it is not ready.
  private micReadySince: number | null = null

  // Delivery evidence.
  private localIntervals: SpeechInterval[] = []
  private localSpeaking = false
  private serverSpeaking = false
  private serverConfirmations: number[] = []
  // Server-confirmed speaking periods, for the uncaptioned-speech check.
  private serverIntervals: SpeechInterval[] = []
  // When LiveKit last named us an active speaker, never trimmed, for telemetry.
  private lastServerSpeakingAt: number | null = null

  // Transcript-flow evidence.
  private ownTranscripts: number[] = []
  // When the last own transcript arrived, never trimmed, for telemetry.
  private lastOwnTranscriptAt: number | null = null
  private combinedSpeakingSince: number | null = null
  private accumulatedSpeechMS = 0
  private transcriptSeenDuringAccumulation = false
  private transcriptWaitDeadline: number | null = null
  private waitSpeechMS = 0
  private waitStartedAt: number | null = null

  // Solo safeguard.
  private soloDiscussionActive = false
  private transcriptionExpected = true
  private soloAccumulatedMS = 0
  private soloEligibleSince: number | null = null

  // Heartbeat.
  private localIdentity: string | null = null
  private currentTrackSid: string | null = null
  private secretaryPresent = false
  private hbRunId: string | null = null
  private hbSeq = -1
  private hbLastStatusAt: number | null = null
  private hbLastTrackRowAt: number | null = null
  private hbPackets = 0
  private hbFramesAboveGate = 0
  private hbDecodeErrors = 0
  private hbAudibleMeasured = false
  private hbSpeechStarts = 0
  private hbSpeechStartsMeasured = false
  private hbLastSpeechStartAdvanceAt: number | null = null
  private hbLastPacketAdvanceAt: number | null = null
  private hbLastAudibleAdvanceAt: number | null = null
  // The next matching row must be taken as a baseline, not progress. Set after a
  // track SID change (reportTrackSid) or a capture restart so the server's
  // already-accumulated counters are not counted as a delta or as fresh progress.
  private hbNeedsBaseline = true
  // Deltas since the previous heartbeat, for the observation telemetry.
  private hbPacketsDelta = 0
  private hbAudibleDelta = 0
  private hbDecodeErrorsDelta = 0
  private currentObservation: HeartbeatObservationKind | null = null
  private observationStartedAt: number | null = null

  // Notice / incident.
  private incidentStartedAt: number | null = null
  private dialogDismissed = false
  private previousShownCause: ProblemCause | 'none' = 'none'
  private lastNotificationsEnabled = false

  // Collected during a single evaluate() and flushed once priority is known.
  private newlyConfirmed: NewlyConfirmed[] = []

  constructor(deps: SoundcheckControllerDeps) {
    const { meeting } = deps

    this.now = deps.now ?? (() => Date.now())
    this.setTimer = deps.setTimer ?? ((fn, ms) => setTimeout(fn, ms))
    this.clearTimer = deps.clearTimer ?? ((handle) => clearTimeout(handle))

    const config =
      deps.config ??
      (meeting ? () => meeting.repository.soundcheckSettings.settings : null)
    if (!config) {
      throw new Error('SoundcheckController requires a config or a meeting')
    }
    this.config = config

    // A real browser MicCheck by default; tests inject a fake.
    this.mic = deps.mic ?? new MicCheck(browserMicCheckDeps(config))

    // Telemetry through the meeting's passive logger so every row carries the
    // room envelope. Without a meeting (and no override) it stays a no-op.
    this.telemetry =
      deps.telemetry ??
      (meeting
        ? createSoundcheckTelemetry(meeting.logEventPassive, () => ({
            trackSid: this.trackSid,
            micState: this.mic.report.state,
          }))
        : NOOP_TELEMETRY)

    makeObservable<
      SoundcheckController,
      'micReport' | 'dialogOpen' | 'setState'
    >(this, {
      microphoneProblem: observable.ref,
      deliveryProblem: observable.ref,
      transcriptProblem: observable.ref,
      problem: observable.ref,
      micReport: observable.ref,
      dialogOpen: observable,
      setState: action,
      bannerVisible: computed,
      dialogVisible: computed,
    })
  }

  initialize(): void {
    this.lastNotificationsEnabled = this.config().notificationsEnabled
    this.scheduleTick()
  }

  dispose(): void {
    if (this.disposed) return
    this.disposed = true
    if (this.tickTimer !== null) this.clearTimer(this.tickTimer)
    const now = this.now()
    if (this.incidentStartedAt !== null) {
      this.telemetry.incidentEnded(
        'left',
        (now - this.incidentStartedAt) / 1000,
        this.openCauses(),
        'meeting'
      )
    }
    // Close any live heartbeat observation so its started row has a matching
    // ended row, mirroring the incident_ended above.
    if (
      this.currentObservation !== null &&
      this.observationStartedAt !== null
    ) {
      this.telemetry.observation(this.currentObservation, 'ended', {
        secretaryPresent: this.secretaryPresent,
        heartbeatAgeMS:
          this.hbLastStatusAt === null ? null : now - this.hbLastStatusAt,
        packetsDelta: this.hbPacketsDelta,
        audibleDelta: this.hbAudibleDelta,
        decodeErrorsDelta: this.hbDecodeErrorsDelta,
        seconds: (now - this.observationStartedAt) / 1000,
      })
    }
    this.mic.dispose()
  }

  get notificationsEnabled(): boolean {
    return this.config().notificationsEnabled
  }

  get bannerVisible(): boolean {
    return this.notificationsEnabled && this.problem !== null
  }

  get dialogVisible(): boolean {
    return this.notificationsEnabled && this.problem !== null && this.dialogOpen
  }

  get trackSid(): string | null {
    return this.currentTrackSid
  }

  // --- Inputs -------------------------------------------------------------

  reportMicReport(report: MicReport): void {
    if (this.disposed) return
    const now = this.now()
    if (report.state !== 'ready') this.micReadySince = null
    else if (this.micReadySince === null) this.micReadySince = now
    this.setState(() => {
      this.micReport = report
    })
    this.telemetry.micResult('meeting', report, null)
    this.evaluate(now)
  }

  reportTrackSid(sid: string | null): void {
    if (sid === this.currentTrackSid) return
    this.currentTrackSid = sid
    // A new track resets the delivery and heartbeat evidence tied to the old one.
    this.localIntervals = []
    this.serverConfirmations = []
    this.serverIntervals = this.openServerIntervalIfSpeaking()
    this.resetHeartbeatBaseline()
  }

  /**
   * LiveKit restarted capture under the same SID (device change, recovery from
   * an ended track), so reportTrackSid never fires. Server speech heard on the
   * old capture says nothing about the new one: kept, it blocked the unreachable
   * check for a full reachability window after a Safari AirPods restart that
   * sent no packets. Audible frames the Secretary counted on the old capture say
   * nothing either, and the next heartbeat can still carry some of them, so it
   * is taken as a baseline.
   */
  reportTrackRestarted(): void {
    this.localIntervals = []
    this.serverConfirmations = []
    this.serverIntervals = this.openServerIntervalIfSpeaking()
    this.hbLastAudibleAdvanceAt = null
    this.hbNeedsBaseline = true
  }

  /**
   * The server-speech evidence a fresh track starts with. LiveKit sends a new
   * active-speaker update only when the list changes, so a student still named
   * across the swap gets no second true: without an open interval here their
   * speech on the new track would never count.
   */
  private openServerIntervalIfSpeaking(): SpeechInterval[] {
    if (!this.serverSpeaking) return []
    return [{ start: this.now(), end: null }]
  }

  setLocalIdentity(identity: string | null): void {
    this.localIdentity = identity
  }

  reportLocalSpeechStart(): void {
    if (this.disposed) return
    const now = this.now()
    this.localSpeaking = true
    this.localIntervals.push({ start: now, end: null })
    this.updateCombinedSpeaking(now)
    this.evaluate(now)
  }

  reportLocalSpeechEnd(): void {
    if (this.disposed) return
    const now = this.now()
    this.localSpeaking = false
    const open = this.localIntervals.find((i) => i.end === null)
    if (open) open.end = now
    this.updateCombinedSpeaking(now)
    this.evaluate(now)
  }

  reportServerSpeaking(speaking: boolean): void {
    if (this.disposed) return
    const now = this.now()
    if (speaking && !this.serverSpeaking) {
      this.serverIntervals.push({ start: now, end: null })
    } else if (!speaking && this.serverSpeaking) {
      const open = this.serverIntervals.find((i) => i.end === null)
      if (open) open.end = now
    }
    this.serverSpeaking = speaking
    if (speaking) {
      this.serverConfirmations.push(now)
      this.lastServerSpeakingAt = now
    }
    this.updateCombinedSpeaking(now)
    this.evaluate(now)
  }

  reportTranscript(): void {
    if (this.disposed) return
    const now = this.now()
    this.ownTranscripts.push(now)
    this.lastOwnTranscriptAt = now
    this.transcriptSeenDuringAccumulation = true
    // Any accepted own transcript progress clears the wait, resets accumulated
    // speech and solo time, and resolves no_transcript.
    this.transcriptWaitDeadline = null
    this.waitStartedAt = null
    this.accumulatedSpeechMS = 0
    this.soloAccumulatedMS = 0
    if (this.soloEligibleSince !== null) this.soloEligibleSince = now
    // A caption is recovery evidence for a confirmed transcript failure. It need
    // not correspond to the speech that started the wait.
    if (this.transcriptProblem !== null) {
      this.resolveLayer('transcript', this.transcriptProblem, 'caption', now)
      this.transcriptProblem = null
    }
    this.evaluate(now)
  }

  reportSecretaryPresence(present: boolean): void {
    if (this.disposed) return
    this.secretaryPresent = present
    this.evaluate(this.now())
  }

  reportSoloDiscussion(active: boolean): void {
    if (this.disposed) return
    const now = this.now()
    this.accrueSolo(now)
    this.soloDiscussionActive = active
    this.evaluate(now)
  }

  reportTranscriptionExpected(expected: boolean): void {
    if (this.disposed) return
    const now = this.now()
    this.accrueSolo(now)
    this.transcriptionExpected = expected
    this.evaluate(now)
  }

  reportHeartbeat(status: SecretaryStatus): void {
    if (this.disposed) return
    const now = this.now()
    if (status.runId !== this.hbRunId) {
      // A new worker run: the first snapshot is status, not progress.
      this.resetHeartbeatBaseline()
      this.hbRunId = status.runId
      this.hbSeq = status.seq
      this.hbLastStatusAt = now
      this.applyHeartbeatRow(status, now, true)
      this.evaluate(now)
      return
    }
    if (status.seq <= this.hbSeq) return // older or duplicate within the run
    this.hbSeq = status.seq
    this.hbLastStatusAt = now
    this.applyHeartbeatRow(status, now, false)
    this.evaluate(now)
  }

  // --- Notice actions -----------------------------------------------------

  dismissDialog(): void {
    if (this.disposed || !this.dialogOpen || this.problem === null) return
    const seconds =
      this.incidentStartedAt === null
        ? 0
        : (this.now() - this.incidentStartedAt) / 1000
    this.setState(() => {
      this.dialogOpen = false
    })
    this.dialogDismissed = true
    this.telemetry.dialogDismissed(this.problem.cause, seconds)
  }

  /** Record a requested reconnect's outcome. Does not itself close the incident. */
  reportRepairResult(
    cause: ProblemCause,
    republished: boolean,
    errorName: string,
    errorMessageSanitized: string
  ): void {
    this.telemetry.repairResult(
      cause,
      republished,
      errorName,
      errorMessageSanitized
    )
  }

  openDialog(): void {
    if (this.disposed || this.dialogOpen || this.problem === null) return
    this.setState(() => {
      this.dialogOpen = true
    })
    this.telemetry.dialogShown(this.problem.cause, 'banner')
  }

  // --- Internals ----------------------------------------------------------

  private setState(mutate: () => void): void {
    mutate()
  }

  private scheduleTick(): void {
    if (this.disposed) return
    this.tickTimer = this.setTimer(() => {
      this.tickTimer = null
      this.evaluate(this.now())
      this.scheduleTick()
    }, this.config().tickMS)
  }

  private updateCombinedSpeaking(now: number): void {
    const speaking = this.localSpeaking || this.serverSpeaking
    if (speaking && this.combinedSpeakingSince === null) {
      this.combinedSpeakingSince = now
      this.transcriptSeenDuringAccumulation = false
    } else if (!speaking && this.combinedSpeakingSince !== null) {
      this.accumulatedSpeechMS += now - this.combinedSpeakingSince
      this.combinedSpeakingSince = null
      this.maybeStartTranscriptWait(now)
    }
  }

  private maybeStartTranscriptWait(now: number): void {
    if (this.transcriptWaitDeadline !== null) return // more speech never postpones
    const config = this.config()
    if (this.transcriptSeenDuringAccumulation) {
      // Flow was shown while they spoke; no further event is required.
      this.accumulatedSpeechMS = 0
      return
    }
    if (this.accumulatedSpeechMS < config.transcriptMinSpeechMS) return
    this.waitSpeechMS = this.accumulatedSpeechMS
    this.waitStartedAt = now
    this.transcriptWaitDeadline = now + config.noTranscriptAfterMS
  }

  private accrueSolo(now: number): void {
    this.soloAccumulatedMS = accrueElapsed(
      this.soloAccumulatedMS,
      this.soloEligibleSince,
      now
    )
    const eligible =
      this.soloDiscussionActive &&
      this.transcriptionExpected &&
      this.micReport.state === 'ready'
    this.soloEligibleSince = eligible ? now : null
  }

  private evaluate(now: number): void {
    if (this.disposed) return
    this.newlyConfirmed = []
    this.accrueSolo(now)
    this.trimWindows(now)
    this.updateHeartbeatObservation(now)
    this.updateMicrophoneProblem(now)
    this.updateDeliveryProblem(now)
    this.updateTranscriptProblem(now)

    this.setState(() => {
      this.problem =
        this.microphoneProblem ?? this.deliveryProblem ?? this.transcriptProblem
    })

    this.flushProblemLogs(now)
    this.updateNotice(now)
  }

  private trimWindows(now: number): void {
    const windowStart = now - this.config().reachabilityWindowMS
    this.serverConfirmations = this.serverConfirmations.filter(
      (t) => t >= windowStart
    )
    this.ownTranscripts = this.ownTranscripts.filter((t) => t >= windowStart)
    this.localIntervals = this.localIntervals.filter(
      (i) => (i.end ?? now) >= windowStart
    )
    this.serverIntervals = this.serverIntervals.filter(
      (i) => (i.end ?? now) >= windowStart
    )
  }

  private updateMicrophoneProblem(now: number): void {
    const r = this.micReport
    if (r.state === 'failed') {
      if (this.microphoneProblem === null) {
        this.microphoneProblem = { cause: r.cause, since: now }
        this.newlyConfirmed.push({
          layer: 'microphone',
          cause: r.cause,
          evidence: {},
        })
      } else if (this.microphoneProblem.cause !== r.cause) {
        // The reason changed: update details, keep the incident, claim no recovery.
        this.microphoneProblem = {
          cause: r.cause,
          since: this.microphoneProblem.since,
        }
        this.newlyConfirmed.push({
          layer: 'microphone',
          cause: r.cause,
          evidence: {},
        })
      }
    } else if (r.state === 'ready') {
      if (this.microphoneProblem !== null) {
        this.resolveLayer(
          'microphone',
          this.microphoneProblem,
          'mic_ready',
          now
        )
        this.microphoneProblem = null
      }
    }
    // checking / inactive: keep any unresolved microphone problem as-is.
  }

  private updateDeliveryProblem(now: number): void {
    const config = this.config()
    const windowStart = now - config.reachabilityWindowMS

    if (this.deliveryProblem !== null) {
      const since = this.deliveryProblem.since
      const serverAfter = this.serverConfirmations.some((t) => t >= since)
      const transcriptAfter = this.ownTranscripts.some((t) => t >= since)
      const heartbeatAfter = this.heartbeatSpeechProgressedSince(since)
      if (serverAfter || transcriptAfter || heartbeatAfter) {
        const by: ResolvedBy = transcriptAfter
          ? 'caption'
          : serverAfter
            ? 'server_speech'
            : 'heartbeat_speech'
        this.resolveLayer('delivery', this.deliveryProblem, by, now)
        this.deliveryProblem = null
      }
    }

    // Confirm only with a working local microphone; checking/inactive pauses it.
    if (this.deliveryProblem === null && this.micReport.state === 'ready') {
      const speechMS = totalSpeechInWindow(
        this.localIntervals,
        windowStart,
        now
      )
      const firstSpeechAt = firstSpeechStartInWindow(
        this.localIntervals,
        windowStart,
        now
      )
      // A confirmation is timestamped when an active-speaker update names us. A
      // flag that stays on through speech longer than the window may bring no
      // newer update, so a flag that is still on counts too.
      const serverConfirmedInWindow =
        this.serverSpeaking || this.serverConfirmations.length > 0
      const ownTranscriptInWindow = this.ownTranscripts.length > 0
      const heartbeatSpeechInWindow =
        this.heartbeatSpeechProgressedSince(windowStart)
      if (
        shouldConfirmDelivery({
          speechMS,
          firstSpeechAt,
          now,
          serverConfirmedInWindow,
          ownTranscriptInWindow,
          heartbeatSpeechInWindow,
          minSpeechMS: config.reachabilityMinSpeechMS,
          delayMS: config.reachabilityDelayMS,
        })
      ) {
        this.deliveryProblem = { cause: 'unreachable', since: now }
        this.newlyConfirmed.push({
          layer: 'delivery',
          cause: 'unreachable',
          evidence: {
            speechMS,
            serverConfirmations: this.serverConfirmations.length,
          },
        })
      }
    }
  }

  private updateTranscriptProblem(now: number): void {
    if (!this.transcriptionExpected) return
    const config = this.config()
    const speechExpired = transcriptWaitExpired(
      this.transcriptWaitDeadline,
      now
    )
    const soloTripped = soloSafeguardTripped(
      this.soloAccumulatedMS,
      config.unmutedNoTranscriptAfterMS
    )
    const notListeningMS = this.notListeningWhileSpeakingMS(now)
    const notListeningTripped = notListeningMS >= config.notListeningConfirmMS
    const serverSpeechMS = uncaptionedServerSpeechMS({
      intervals: this.serverIntervals,
      windowStart: now - config.reachabilityWindowMS,
      now,
      lastCaptionAt: this.lastOwnTranscriptAt,
      delayMS: config.reachabilityDelayMS,
    })
    const uncaptionedTripped = serverSpeechMS >= config.serverSpeechNoCaptionMS
    const confirmed =
      speechExpired || soloTripped || notListeningTripped || uncaptionedTripped
    if (confirmed && this.transcriptProblem === null) {
      this.transcriptProblem = { cause: 'no_transcript', since: now }
      this.newlyConfirmed.push({
        layer: 'transcript',
        cause: 'no_transcript',
        evidence: {
          speechMS: speechExpired ? this.waitSpeechMS : undefined,
          soloMS: soloTripped ? this.soloAccumulatedMS : undefined,
          notListeningMS: notListeningTripped ? notListeningMS : undefined,
          serverSpeechMS: uncaptionedTripped ? serverSpeechMS : undefined,
          msSincePause:
            speechExpired && this.waitStartedAt !== null
              ? now - this.waitStartedAt
              : undefined,
        },
      })
      // Failure recorded; stop the deadline so it does not re-fire every tick.
      // Detection keeps running for the rest of the session.
      this.transcriptWaitDeadline = null
      this.waitStartedAt = null
      // Drain the solo accumulator too, so a later recovery does not immediately
      // re-trip on a stale count. It re-accumulates from now if still eligible.
      if (soloTripped) {
        this.soloAccumulatedMS = 0
        if (this.soloEligibleSince !== null) this.soloEligibleSince = now
      }
    }
  }

  /**
   * How long the Secretary's status has left this microphone out since the
   * student first spoke into it, or 0 when they have not. A fresh status
   * without our track means the worker is running but not transcribing us.
   * The clock starts at that first speech, not when the track dropped out, so
   * quiet time before it does not count; and after the latest own caption, so
   * a caption that resolves no_transcript does not leave it to reopen at once.
   */
  private notListeningWhileSpeakingMS(now: number): number {
    const observedSince = this.observationStartedAt
    if (this.currentObservation !== 'not_listening' || observedSince === null) {
      return 0
    }
    if (this.micReport.state !== 'ready') return 0
    const countFrom = Math.max(
      observedSince,
      this.lastOwnTranscriptAt ?? observedSince
    )
    const localSpeechAt = firstSpeechStartInWindow(
      this.localIntervals,
      countFrom,
      now
    )
    const serverSpeechAt = this.serverConfirmations.find((t) => t >= countFrom)
    const speechTimes = [localSpeechAt, serverSpeechAt ?? null].filter(
      (t): t is number => t !== null
    )
    if (speechTimes.length === 0) return 0
    return now - Math.min(...speechTimes)
  }

  private resolveLayer(
    layer: ProblemLayer,
    problem: Problem,
    resolvedBy: ResolvedBy,
    now: number
  ): void {
    const secondsOpen = (now - problem.since) / 1000
    this.telemetry.resolved(
      layer,
      problem.cause,
      secondsOpen,
      resolvedBy,
      'meeting'
    )
  }

  private flushProblemLogs(now: number): void {
    const currentLayer = this.currentLayer()
    for (const nc of this.newlyConfirmed) {
      const shown = currentLayer === nc.layer
      this.telemetry.problem(
        nc.layer,
        nc.cause,
        shown,
        {
          ...nc.evidence,
          heartbeatObservation: this.currentObservation ?? 'none',
          secretaryPresent: this.secretaryPresent,
          ownCaptionAgeMS: msSince(this.lastOwnTranscriptAt, now),
          serverSpeakingAgeMS: msSince(this.lastServerSpeakingAt, now),
          heartbeatAudibleAgeMS: msSince(this.hbLastAudibleAdvanceAt, now),
        },
        'meeting'
      )
    }
    this.newlyConfirmed = []
  }

  private currentLayer(): ProblemLayer | null {
    if (this.problem === null) return null
    if (this.problem === this.microphoneProblem) return 'microphone'
    if (this.problem === this.deliveryProblem) return 'delivery'
    return 'transcript'
  }

  private updateNotice(now: number): void {
    const notificationsEnabled = this.notificationsEnabled
    const hasProblem = this.problem !== null

    // Problem selection change.
    const shownCause: ProblemCause | 'none' = this.problem?.cause ?? 'none'
    if (shownCause !== this.previousShownCause) {
      this.telemetry.problemSelected(this.previousShownCause, shownCause)
      this.previousShownCause = shownCause
    }

    // Incident lifecycle.
    if (hasProblem && this.incidentStartedAt === null) {
      this.incidentStartedAt = now
      this.dialogDismissed = false
      if (notificationsEnabled && this.problem) {
        this.setState(() => {
          this.dialogOpen = true
        })
        this.telemetry.dialogShown(this.problem.cause, 'auto')
      }
    } else if (!hasProblem && this.incidentStartedAt !== null) {
      const seconds = (now - this.incidentStartedAt) / 1000
      this.telemetry.incidentEnded('recovered', seconds, '', 'meeting')
      this.incidentStartedAt = null
      this.dialogDismissed = false
      this.setState(() => {
        this.dialogOpen = false
      })
    }

    // Notifications turned on mid-incident opens the current incident once.
    if (
      notificationsEnabled &&
      !this.lastNotificationsEnabled &&
      hasProblem &&
      !this.dialogOpen &&
      !this.dialogDismissed &&
      this.problem
    ) {
      this.setState(() => {
        this.dialogOpen = true
      })
      this.telemetry.dialogShown(this.problem.cause, 'notifications_enabled')
    }
    this.lastNotificationsEnabled = notificationsEnabled
  }

  private openCauses(): string {
    return [
      this.microphoneProblem,
      this.deliveryProblem,
      this.transcriptProblem,
    ]
      .filter((p): p is Problem => p !== null)
      .map((p) => p.cause)
      .join(',')
  }

  // --- Heartbeat ----------------------------------------------------------

  private resetHeartbeatBaseline(): void {
    this.hbPackets = 0
    this.hbFramesAboveGate = 0
    this.hbDecodeErrors = 0
    this.hbAudibleMeasured = false
    this.hbSpeechStarts = 0
    this.hbSpeechStartsMeasured = false
    this.hbLastSpeechStartAdvanceAt = null
    this.hbLastPacketAdvanceAt = null
    this.hbLastAudibleAdvanceAt = null
    this.hbLastTrackRowAt = null
    this.hbNeedsBaseline = true
    this.hbPacketsDelta = 0
    this.hbAudibleDelta = 0
    this.hbDecodeErrorsDelta = 0
  }

  private applyHeartbeatRow(
    status: SecretaryStatus,
    now: number,
    baseline: boolean
  ): void {
    const row = status.tracks.find(
      (t) =>
        t.identity === this.localIdentity && t.trackId === this.currentTrackSid
    )
    if (!row) {
      this.hbPacketsDelta = 0
      this.hbAudibleDelta = 0
      this.hbDecodeErrorsDelta = 0
      return
    }
    this.hbLastTrackRowAt = now

    // A counter that went backwards means a fresh baseline; its snapshot is not
    // progress.
    const rewound =
      row.packets < this.hbPackets ||
      (row.framesAboveGate ?? 0) < this.hbFramesAboveGate ||
      (row.speechStarts ?? 0) < this.hbSpeechStarts ||
      row.decodeErrors < this.hbDecodeErrors

    if (baseline || rewound || this.hbNeedsBaseline) {
      this.hbPackets = row.packets
      this.hbFramesAboveGate = row.framesAboveGate ?? 0
      this.hbDecodeErrors = row.decodeErrors
      this.hbAudibleMeasured = row.framesAboveGate !== undefined
      this.hbSpeechStarts = row.speechStarts ?? 0
      this.hbSpeechStartsMeasured = row.speechStarts !== undefined
      this.hbNeedsBaseline = false
      this.hbPacketsDelta = 0
      this.hbAudibleDelta = 0
      this.hbDecodeErrorsDelta = 0
      return
    }

    if (row.packets > this.hbPackets) this.hbLastPacketAdvanceAt = now
    if (
      row.framesAboveGate !== undefined &&
      row.framesAboveGate > this.hbFramesAboveGate
    ) {
      this.hbLastAudibleAdvanceAt = now
    }
    if (row.framesAboveGate !== undefined) this.hbAudibleMeasured = true
    if (row.speechStarts !== undefined) {
      if (row.speechStarts > this.hbSpeechStarts) {
        this.hbLastSpeechStartAdvanceAt = now
      }
      this.hbSpeechStarts = row.speechStarts
      this.hbSpeechStartsMeasured = true
    }
    this.hbPacketsDelta = Math.max(0, row.packets - this.hbPackets)
    this.hbAudibleDelta =
      row.framesAboveGate === undefined
        ? 0
        : Math.max(0, row.framesAboveGate - this.hbFramesAboveGate)
    this.hbDecodeErrorsDelta = Math.max(
      0,
      row.decodeErrors - this.hbDecodeErrors
    )

    this.hbPackets = row.packets
    if (row.framesAboveGate !== undefined) {
      this.hbFramesAboveGate = row.framesAboveGate
    }
    this.hbDecodeErrors = row.decodeErrors
  }

  private heartbeatSpeechProgressedSince(t: number): boolean {
    return (
      this.hbLastAudibleAdvanceAt !== null && this.hbLastAudibleAdvanceAt >= t
    )
  }

  private heartbeatState(now: number): HeartbeatState {
    const windowStart = now - this.config().reachabilityWindowMS
    // A muted or checking mic sends no packets, so speech from before the mute
    // must not make the stalled counters look like a delivery failure.
    const micReady = this.micReadySince !== null
    const expectingSpeech =
      micReady && this.localIntervals.some((i) => (i.end ?? now) >= windowStart)
    // The counters stalled while the mic was not ready, and a baseline row is
    // never progress. Treat the moment the mic became ready as progress, so
    // joining, unmuting, or a restart gets the full timeout to show packets.
    return {
      secretaryPresent: this.secretaryPresent,
      lastStatusAt: this.hbLastStatusAt,
      lastTrackRowAt: this.hbLastTrackRowAt,
      lastPacketAdvanceAt: latest(
        this.hbLastPacketAdvanceAt,
        this.micReadySince
      ),
      lastAudibleAdvanceAt: latest(
        this.hbLastAudibleAdvanceAt,
        this.micReadySince
      ),
      audibleMeasured: this.hbAudibleMeasured,
      lastSpeechStartAdvanceAt: latest(
        this.hbLastSpeechStartAdvanceAt,
        this.micReadySince
      ),
      speechStartsMeasured: this.hbSpeechStartsMeasured,
      decodeErrorsDelta: this.hbDecodeErrorsDelta,
      expectingSpeech,
    }
  }

  private updateHeartbeatObservation(now: number): void {
    if (!this.transcriptionExpected) return
    const config = this.config()
    const kind = heartbeatObservation(
      this.heartbeatState(now),
      now,
      config.heartbeatTimeoutMS,
      config.reachabilityWindowMS
    )
    if (kind === this.currentObservation) return

    const detail = {
      secretaryPresent: this.secretaryPresent,
      heartbeatAgeMS:
        this.hbLastStatusAt === null ? null : now - this.hbLastStatusAt,
      packetsDelta: this.hbPacketsDelta,
      audibleDelta: this.hbAudibleDelta,
      decodeErrorsDelta: this.hbDecodeErrorsDelta,
    }
    if (
      this.currentObservation !== null &&
      this.observationStartedAt !== null
    ) {
      this.telemetry.observation(this.currentObservation, 'ended', {
        ...detail,
        seconds: (now - this.observationStartedAt) / 1000,
      })
    }
    this.currentObservation = kind
    this.observationStartedAt = kind === null ? null : now
    if (kind !== null) {
      this.telemetry.observation(kind, 'started', detail)
    }
  }
}

function latest(first: number | null, second: number | null): number | null {
  if (first === null) return second
  if (second === null) return first
  return Math.max(first, second)
}

function msSince(at: number | null, now: number): number | null {
  return at === null ? null : now - at
}
